import { useState, useEffect, useRef } from 'react';
import { AppDocument } from 'api';
import IconBtn from 'components/common/IconBtn';
import BaseInput from 'components/common/BaseInput';
import { ReactComponent as TagIcon } from 'assets/icons/B2B/error_outline.svg';
import * as S from './ViewEditDocumentPopoverMenu.styled';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PrimaryButton from 'components/common/PrimaryButton/PrimaryButton';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { InfoIcon } from 'views/Dashboards-B2B/components/Table.styled';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
    '$. MuiPaper-root-MuiDialog-paper': {
        margin: '0px',
    },
}));

const HtmlTooltipContent = ({ html }: { html: string }) => (
    <Box
        sx={{
            '& p': { my: 0.5 },
            '& ul, & ol': { margin: '4px 0', paddingLeft: '18px' },
            '& li': { margin: '2px 0' },
        }}
        dangerouslySetInnerHTML={{ __html: html }}
    />
);

type EditDocumentPopoverMenuProps = {
    document: AppDocument;
    handleUpdateDoc: (document: AppDocument, title: string, explanation: string | null) => void;
    mobile: boolean;
    icon: boolean;
    handleCloseActionsPopover?: () => void;
};

const ViewEditDocumentPopoverMenu = ({
    document,
    handleUpdateDoc,
    mobile,
    icon,
    handleCloseActionsPopover,
}: EditDocumentPopoverMenuProps) => {
    const quillRef = useRef<ReactQuill | null>(null);
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const [title, setTitle] = useState<string>(document.document_name);
    const [explanation, setExplanation] = useState<string>(
        document.explanation ? document.explanation : ''
    );
    const [open, setOpen] = useState(false);
    const maxLengthTitle = 1000;
    const maxLength = 2000;
    const [remainingCharacters, setRemainingCharacters] = useState<number>(0);

    const handleClickOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };

    const handleSave = () => {
        handleUpdateDoc(document, title, explanation);
        handleClose();
        handleCloseActionsPopover?.();
    };

    const handleChange = (value: string) => {
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            // there is always one newline character in quill so we have to deduct 1
            const textLength = quill.getLength() - 1;
            if (textLength <= maxLength) {
                setExplanation(value);
                setRemainingCharacters(maxLength - textLength);
            } else {
                quill.deleteText(maxLength, textLength - maxLength);
            }
        }
    };

    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value.length <= maxLengthTitle) {
            setTitle(event.target.value);
        }
    };

    useEffect(() => {
        if (quillRef.current) {
            const editorContainer = quillRef.current.getEditor().root;
            if (editorContainer) {
                editorContainer.style.fontFamily = 'Assistant, sans-serif';
                editorContainer.style.fontSize = '15px';
                editorContainer.style.lineHeight = '20px';
            }

            const quill = quillRef.current.getEditor();
            // there is always one newline character in quill so we have to deduct 1
            const textLength = quill.getLength() - 1;
            setRemainingCharacters(maxLength - textLength);
        } else {
            setRemainingCharacters(maxLength - explanation.length);
        }
    }, [explanation]);

    return (
        <>
            {icon ? (
                <div style={{ outline: 'none' }} onClick={handleClickOpen}>
                    <IconBtn
                        style={{
                            padding: '0px',
                            height: '20px',
                            width: '20px',
                            marginRight: '10px',
                        }}
                    >
                        <TagIcon fill="#282828" />
                    </IconBtn>
                    <S.TextForActionsDropdown>
                        {t('editDetails', { lng: language })}
                    </S.TextForActionsDropdown>
                </div>
            ) : (
                <Tooltip
                    title={
                        document.explanation ? (
                            <HtmlTooltipContent html={document.explanation} />
                        ) : (
                            ''
                        )
                    }
                >
                    <S.Explanation onClick={handleClickOpen}>
                        <InfoIcon open={open} />
                    </S.Explanation>
                </Tooltip>
            )}

            <BootstrapDialog
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open}
            >
                <S.Title>
                    {t('editDetails', { lng: language })}
                    <S.CloseIcon onClick={handleClose} />
                </S.Title>
                <S.Container mobile={mobile}>
                    <BaseInput
                        fullWidth
                        size="small"
                        value={title}
                        onChange={handleTitleChange}
                        label={`*${t('documentName', {
                            lng: language,
                        })}: ${title?.length} / ${maxLengthTitle}`}
                    />
                    <S.FullExplanation>
                        <Typography
                            sx={{
                                color: '#282828',
                                fontFamily: 'Assistant, sans-serif',
                                fontSize: '12px',
                                fontWeight: 400,
                                whiteSpace: 'pre',
                            }}
                            variant="caption"
                            color="textSecondary"
                        >
                            {`${'      '}*${t('documentExplanation', {
                                lng: language,
                            })}: ${maxLength - remainingCharacters} / ${maxLength}`}
                        </Typography>

                        <ReactQuill
                            ref={quillRef}
                            theme="snow"
                            value={explanation}
                            onChange={handleChange}
                        />
                    </S.FullExplanation>
                    <S.Row style={{ justifyContent: 'flex-end' }}>
                        <PrimaryButton
                            variant="outlined"
                            size="min"
                            color="blue"
                            mr="20px"
                            onClick={handleClose}
                        >
                            {t('close', { lng: language })}
                        </PrimaryButton>
                        <PrimaryButton
                            variant="contained"
                            size="min"
                            color="blue"
                            mr="20px"
                            disabled={
                                document.document_name === title &&
                                document.explanation === explanation
                            }
                            onClick={handleSave}
                        >
                            {t('save', { lng: language })}
                        </PrimaryButton>
                    </S.Row>
                </S.Container>
            </BootstrapDialog>
        </>
    );
};

export default ViewEditDocumentPopoverMenu;
